import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useApolloClient } from '@apollo/client';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import isEmpty from 'lodash/isEmpty';

import { graphQLIdGenerator } from 'helpers/graphql-utils';

import TagsModal from './TagsModal';
import { recordAddedAndRemovedTags } from '../helpers/tagHelpers';
import { GQL_GET_PROJECT_TAGS } from '../../assetFeed/FilterRow/TagRow';
import {
  updateTagsCacheSetEdges,
  GQL_GET_TAGS,
} from '../graphql/TagsDataProvider';
import { GQL_ALTER_TAGS } from '../graphql/AlterTags.graphql';

const recordablesFromData = (data) =>
  get(data, 'recordablesAlterTags.recordables', []);

const tagsFromRecordable = (recordable) => get(recordable, 'tags.edges', []);

const errorsFromData = (data) => get(data, 'recordablesAlterTags.errors', []);

const updateCache = ({ cache, data }) => {
  const gqlErrors = errorsFromData(data);
  if (gqlErrors.length) {
    return;
  }

  const recordables = recordablesFromData(data);
  recordables.forEach((recordable) => {
    const newEdges = tagsFromRecordable(recordable);
    const nodeId = recordable.id;
    updateTagsCacheSetEdges({ cache, newEdges, nodeId });
  });
};

const TagsModalWithMutation = (props) => {
  // * this is gross, but the modal doesn't have context with the router so we can't use useParams
  const url = window.location.href;
  const projectId = url.match(/\/projects\/(\d+)/)?.[1];
  const projectNodeId = projectId
    ? graphQLIdGenerator('Location', projectId)
    : null;

  const client = useApolloClient();
  const { nodeIds } = props;
  const [oldRecordables, setOldRecordables] = useState(null);
  const refetchQueries = [];

  if (projectNodeId) {
    refetchQueries.push({
      query: GQL_GET_PROJECT_TAGS,
      variables: {
        id: projectNodeId,
      },
    });
  }

  useEffect(() => {
    const getRecordables = (recordableIds) => {
      try {
        return keyBy(
          recordableIds.map((nodeId) => {
            return client.readQuery({
              query: GQL_GET_TAGS,
              variables: { nodeId },
            });
          }),
          (value) => value.node.id,
        );
      } catch (e) {
        return {}; // not a critical error if we can't record tag applied events
      }
    };

    setOldRecordables(getRecordables(nodeIds));
  }, [nodeIds, client]);

  const [alterTags] = useMutation(GQL_ALTER_TAGS, {
    onCompleted: (data, variables) => {
      const keyedData = keyBy(recordablesFromData(data), (value) => value.id);
      const newRecordables = isEmpty(oldRecordables) ? data : keyedData;
      recordAddedAndRemovedTags(
        oldRecordables,
        newRecordables,
        variables.variables.input.tags,
      );
    },
    update: (cache, { data }) => updateCache({ cache, data }),
    refetchQueries,
  });

  const saveTags = (input) => {
    alterTags({
      variables: {
        input,
      },
    });
  };

  return <TagsModal alterTags={saveTags} {...props} />;
};

TagsModalWithMutation.propTypes = {
  nodeIds: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default TagsModalWithMutation;
